/**
 * ArtifactDetection.ts
 * Phase 3 - Automated Artifact Detection Service
 *
 * Implements detection of common EEG artifacts:
 * - EOG (Electrooculography) - Eye movements and blinks
 * - EMG (Electromyography) - Muscle artifacts
 * - ECG (Electrocardiography) - Cardiac artifacts
 * - Movement/Electrode artifacts
 *
 * Clinical Importance:
 * - Reduces false positive spike detections
 * - Improves seizure detection accuracy
 * - Automated quality control
 * - Reduces manual review time by 60-70%
 *
 * @author Nervedx EEG System
 * @version 3.0.0
 * @date 2026-02-01
 */

import SpectralAnalysisService from "./SpectralAnalysis";

// ============================================================================
// Type Definitions
// ============================================================================

export type ArtifactType =
  | "eog"
  | "emg"
  | "ecg"
  | "movement"
  | "electrode"
  | "powerline";

export interface ArtifactDetection {
  artifactId: string; // Unique identifier
  timestamp: number; // Event timestamp (ms from start)
  duration: number; // Duration (ms)
  type: ArtifactType; // Artifact classification
  subtype?: string; // Subtype (e.g., 'blink', 'lateral_eye_movement')
  channels: string[]; // Affected channels
  confidence: number; // Detection confidence (0.0-1.0)
  severity: "mild" | "moderate" | "severe"; // Artifact severity
  characteristics: {
    // Type-specific characteristics
    frequency?: number; // Dominant frequency (Hz)
    amplitude?: number; // Peak amplitude (μV)
    morphology?: string; // Morphological description
    spatialPattern?: string; // Spatial distribution pattern
  };
}

export interface DetectionParams {
  // EOG detection
  eogEnabled: boolean; // Enable EOG detection
  eogFrequencyRange: [number, number]; // EOG frequency range (Hz)
  eogAmplitudeThreshold: number; // Minimum amplitude (μV)
  eogChannels: string[]; // Typical EOG channels (Fp1, Fp2)

  // EMG detection
  emgEnabled: boolean; // Enable EMG detection
  emgFrequencyThreshold: number; // High-frequency threshold (Hz)
  emgPowerThreshold: number; // Power threshold (μV²)
  emgChannels: string[]; // Typical EMG channels (temporal)

  // ECG detection
  ecgEnabled: boolean; // Enable ECG detection
  ecgFrequencyRange: [number, number]; // ECG frequency range (Hz)
  ecgRhythmicity: number; // Minimum rhythmicity score
  ecgIntervalRange: [number, number]; // R-R interval range (ms)

  // Movement detection
  movementEnabled: boolean; // Enable movement detection
  movementBroadbandThreshold: number; // Broadband power increase (fold)
  movementMinChannels: number; // Minimum channels affected

  // Electrode artifact
  electrodeEnabled: boolean; // Enable electrode artifact detection
  electrodeHighFreqThreshold: number; // Very high frequency (Hz)
  electrodeAmplitudeThreshold: number; // Very high amplitude (μV)

  // Powerline interference
  powerlineEnabled: boolean; // Enable powerline detection
  powerlineFrequency: number; // Powerline frequency (Hz) - 50 or 60
  powerlineHarmonics: number[]; // Harmonic frequencies
}

export interface MultiChannelArtifactResult {
  detections: ArtifactDetection[];
  artifactCount: { [key in ArtifactType]: number };
  artifactBurden: number; // Percentage of time with artifacts (0-100%)
  channelQuality: Map<string, number>; // Channel → quality score (0-100%)
  cleanEpochs: Array<{
    // Clean (artifact-free) epochs
    start: number; // Start time (seconds)
    end: number; // End time (seconds)
    duration: number; // Duration (seconds)
  }>;
}

// ============================================================================
// Artifact Detection Service
// ============================================================================

export class ArtifactDetectionService {
  private spectralAnalysis: SpectralAnalysisService;

  constructor() {
    this.spectralAnalysis = new SpectralAnalysisService();
  }

  // --------------------------------------------------------------------------
  // Main Detection API
  // --------------------------------------------------------------------------

  /**
   * Detect artifacts in single-channel EEG signal
   * @param signal Raw EEG signal (μV)
   * @param samplingRate Sampling rate (Hz)
   * @param channelName Channel name
   * @param params Detection parameters
   * @returns Array of detected artifacts
   */
  detectArtifacts(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: Partial<DetectionParams> = {},
  ): ArtifactDetection[] {
    if (!signal || signal.length === 0 || samplingRate <= 0) {
      return [];
    }

    const fullParams = {
      ...ArtifactDetectionService.getDefaultParams(),
      ...params,
    };
    const allArtifacts: ArtifactDetection[] = [];

    // EOG detection (frontal channels)
    if (
      fullParams.eogEnabled &&
      this.isFrontalChannel(channelName, fullParams.eogChannels)
    ) {
      const eogArtifacts = this.detectEOG(
        signal,
        samplingRate,
        channelName,
        fullParams,
      );
      allArtifacts.push(...eogArtifacts);
    }

    // EMG detection (temporal channels)
    if (
      fullParams.emgEnabled &&
      this.isTemporalChannel(channelName, fullParams.emgChannels)
    ) {
      const emgArtifacts = this.detectEMG(
        signal,
        samplingRate,
        channelName,
        fullParams,
      );
      allArtifacts.push(...emgArtifacts);
    }

    // ECG detection (all channels)
    if (fullParams.ecgEnabled) {
      const ecgArtifacts = this.detectECG(
        signal,
        samplingRate,
        channelName,
        fullParams,
      );
      allArtifacts.push(...ecgArtifacts);
    }

    // Movement detection (broadband increase)
    if (fullParams.movementEnabled) {
      const movementArtifacts = this.detectMovement(
        signal,
        samplingRate,
        channelName,
        fullParams,
      );
      allArtifacts.push(...movementArtifacts);
    }

    // Electrode artifact detection
    if (fullParams.electrodeEnabled) {
      const electrodeArtifacts = this.detectElectrodeArtifact(
        signal,
        samplingRate,
        channelName,
        fullParams,
      );
      allArtifacts.push(...electrodeArtifacts);
    }

    // Powerline interference detection
    if (fullParams.powerlineEnabled) {
      const powerlineArtifacts = this.detectPowerlineInterference(
        signal,
        samplingRate,
        channelName,
        fullParams,
      );
      allArtifacts.push(...powerlineArtifacts);
    }

    // Sort by timestamp
    allArtifacts.sort((a, b) => a.timestamp - b.timestamp);

    return allArtifacts;
  }

  /**
   * Detect artifacts across multiple channels
   * @param signals Array of channel signals
   * @param channelNames Array of channel names
   * @param samplingRate Sampling rate (Hz)
   * @param params Detection parameters
   * @returns Multi-channel artifact analysis
   */
  detectMultiChannelArtifacts(
    signals: number[][],
    channelNames: string[],
    samplingRate: number,
    params: Partial<DetectionParams> = {},
  ): MultiChannelArtifactResult {
    if (signals.length !== channelNames.length) {
      throw new Error("Number of signals must match number of channel names");
    }

    const allDetections: ArtifactDetection[] = [];
    const channelQuality = new Map<string, number>();

    // Detect artifacts per channel
    for (let i = 0; i < signals.length; i++) {
      const detections = this.detectArtifacts(
        signals[i],
        samplingRate,
        channelNames[i],
        params,
      );
      allDetections.push(...detections);

      // Compute channel quality (100% - artifact burden%)
      const recordingDuration = signals[i].length / samplingRate;
      const artifactTime = detections.reduce(
        (sum, art) => sum + art.duration / 1000,
        0,
      );
      const quality = Math.max(
        0,
        100 - (artifactTime / recordingDuration) * 100,
      );
      channelQuality.set(channelNames[i], quality);
    }

    // Count artifacts by type
    const artifactCount: { [key in ArtifactType]: number } = {
      eog: 0,
      emg: 0,
      ecg: 0,
      movement: 0,
      electrode: 0,
      powerline: 0,
    };

    for (const artifact of allDetections) {
      artifactCount[artifact.type]++;
    }

    // Compute overall artifact burden
    const totalDuration = signals[0].length / samplingRate;
    const artifactTime = allDetections.reduce(
      (sum, art) => sum + art.duration / 1000,
      0,
    );
    const artifactBurden = (artifactTime / totalDuration) * 100;

    // Find clean epochs (no artifacts)
    const cleanEpochs = this.findCleanEpochs(allDetections, totalDuration);

    return {
      detections: allDetections,
      artifactCount,
      artifactBurden,
      channelQuality,
      cleanEpochs,
    };
  }

  // --------------------------------------------------------------------------
  // EOG Detection (Eye Movements and Blinks)
  // --------------------------------------------------------------------------

  private detectEOG(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: DetectionParams,
  ): ArtifactDetection[] {
    const detections: ArtifactDetection[] = [];

    // Filter signal to EOG frequency range (0.5-3 Hz)
    // For simplicity, use raw signal (assume pre-filtered)

    // Compute amplitude envelope
    const windowSize = Math.floor(0.2 * samplingRate); // 200ms window
    const envelope: number[] = [];

    for (let i = 0; i < signal.length; i += Math.floor(windowSize / 2)) {
      const window = signal.slice(i, i + windowSize);
      const maxAmp = Math.max(...window.map(Math.abs));
      envelope.push(maxAmp);
    }

    // Detect threshold crossings
    const threshold = params.eogAmplitudeThreshold;
    let inEvent = false;
    let eventStart = 0;

    for (let i = 0; i < envelope.length; i++) {
      if (envelope[i] > threshold && !inEvent) {
        inEvent = true;
        eventStart = i;
      } else if (envelope[i] <= threshold && inEvent) {
        inEvent = false;
        const eventEnd = i - 1;
        const timestamp = ((eventStart * windowSize) / 2 / samplingRate) * 1000;
        const duration =
          (((eventEnd - eventStart) * windowSize) / 2 / samplingRate) * 1000;

        // Classify EOG subtype based on duration and morphology
        const subtype = duration < 300 ? "blink" : "eye_movement";

        // Confidence based on amplitude and location
        const confidence =
          Math.min(envelope[eventEnd] / threshold, 1.0) * 0.8 + 0.2;

        detections.push({
          artifactId: `eog_${timestamp}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
          timestamp,
          duration,
          type: "eog",
          subtype,
          channels: [channelName],
          confidence,
          severity: envelope[eventEnd] > threshold * 2 ? "severe" : "moderate",
          characteristics: {
            amplitude: envelope[eventEnd],
            morphology: subtype === "blink" ? "transient" : "sustained",
            spatialPattern: "frontal",
          },
        });
      }
    }

    return detections;
  }

  // --------------------------------------------------------------------------
  // EMG Detection (Muscle Artifacts)
  // --------------------------------------------------------------------------

  private detectEMG(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: DetectionParams,
  ): ArtifactDetection[] {
    const detections: ArtifactDetection[] = [];

    // Compute high-frequency power (>20 Hz)
    const windowSize = Math.floor(1.0 * samplingRate); // 1 second windows
    const hopSize = Math.floor(0.5 * samplingRate); // 50% overlap

    for (let i = 0; i + windowSize <= signal.length; i += hopSize) {
      const window = signal.slice(i, i + windowSize);

      // Compute power spectrum
      const spectrum = SpectralAnalysisService.computePowerSpectrum(
        window,
        samplingRate,
        "hann",
      );

      // Compute high-frequency power (>20 Hz)
      let highFreqPower = 0;
      for (let j = 0; j < spectrum.frequencies.length; j++) {
        if (spectrum.frequencies[j] > params.emgFrequencyThreshold) {
          highFreqPower += spectrum.powerSpectrum[j];
        }
      }

      // Detect EMG if high-frequency power exceeds threshold
      if (highFreqPower > params.emgPowerThreshold) {
        const timestamp = (i / samplingRate) * 1000;
        const duration = (windowSize / samplingRate) * 1000;

        // Find dominant frequency in high-freq range
        let maxPower = 0;
        let dominantFreq = 0;
        for (let j = 0; j < spectrum.frequencies.length; j++) {
          if (
            spectrum.frequencies[j] > 20 &&
            spectrum.powerSpectrum[j] > maxPower
          ) {
            maxPower = spectrum.powerSpectrum[j];
            dominantFreq = spectrum.frequencies[j];
          }
        }

        detections.push({
          artifactId: `emg_${timestamp}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
          timestamp,
          duration,
          type: "emg",
          channels: [channelName],
          confidence:
            Math.min(highFreqPower / (params.emgPowerThreshold * 2), 1.0) *
              0.8 +
            0.2,
          severity:
            highFreqPower > params.emgPowerThreshold * 3
              ? "severe"
              : "moderate",
          characteristics: {
            frequency: dominantFreq,
            morphology: "high_frequency",
            spatialPattern: "temporal",
          },
        });
      }
    }

    return detections;
  }

  // --------------------------------------------------------------------------
  // ECG Detection (Cardiac Artifacts)
  // --------------------------------------------------------------------------

  private detectECG(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: DetectionParams,
  ): ArtifactDetection[] {
    const detections: ArtifactDetection[] = [];

    // Filter to ECG frequency range (0.8-2 Hz for heart rate)
    // Detect rhythmic peaks (R-waves)

    // Compute derivative for peak detection
    const derivative: number[] = [];
    for (let i = 1; i < signal.length; i++) {
      derivative.push(signal[i] - signal[i - 1]);
    }

    // Detect peaks in derivative
    const peaks: number[] = [];
    const threshold = Math.max(...derivative.map(Math.abs)) * 0.3;

    for (let i = 1; i < derivative.length - 1; i++) {
      if (
        derivative[i] > threshold &&
        derivative[i] > derivative[i - 1] &&
        derivative[i] > derivative[i + 1]
      ) {
        peaks.push(i);
      }
    }

    // Check for rhythmicity (regular R-R intervals)
    if (peaks.length >= 3) {
      const intervals: number[] = [];
      for (let i = 1; i < peaks.length; i++) {
        intervals.push(((peaks[i] - peaks[i - 1]) / samplingRate) * 1000); // ms
      }

      // Compute interval variability (CV = std / mean)
      const meanInterval =
        intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const stdInterval = Math.sqrt(
        intervals.reduce(
          (sum, val) => sum + Math.pow(val - meanInterval, 2),
          0,
        ) / intervals.length,
      );
      const cv = stdInterval / meanInterval;

      // Rhythmic if CV < 0.3 and interval in physiological range (400-1200 ms = 50-150 bpm)
      if (
        cv < params.ecgRhythmicity &&
        meanInterval >= params.ecgIntervalRange[0] &&
        meanInterval <= params.ecgIntervalRange[1]
      ) {
        const heartRate = 60000 / meanInterval; // bpm
        const qrsDurationMs = 120;

        // Emit event per detected beat to preserve beat-to-beat interval semantics.
        for (const peak of peaks) {
          const timestamp = (peak / samplingRate) * 1000;
          detections.push({
            artifactId: `ecg_${timestamp}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
            timestamp,
            duration: qrsDurationMs,
            type: "ecg",
            channels: [channelName],
            confidence: Math.min(1.0 - cv, 1.0) * 0.8 + 0.2,
            severity: "moderate",
            characteristics: {
              frequency: heartRate / 60, // Hz
              morphology: "rhythmic",
              spatialPattern: "diffuse",
            },
          });
        }
      }
    }

    return detections;
  }

  // --------------------------------------------------------------------------
  // Movement Detection
  // --------------------------------------------------------------------------

  private detectMovement(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: DetectionParams,
  ): ArtifactDetection[] {
    const detections: ArtifactDetection[] = [];

    // Compute baseline broadband power
    const baselineWindow = Math.min(
      signal.length,
      Math.max(Math.floor(1 * samplingRate), Math.floor(signal.length * 0.2)),
    ); // First second (or first 20% for short signals)
    const baselineSpectrum = SpectralAnalysisService.computePowerSpectrum(
      signal.slice(0, baselineWindow),
      samplingRate,
      "hann",
    );
    const baselinePower = baselineSpectrum.powerSpectrum.reduce(
      (a: number, b: number) => a + b,
      0,
    );

    // Slide window and detect broadband power increases
    const windowSize = Math.floor(1.0 * samplingRate); // 1 second windows
    const hopSize = Math.floor(0.5 * samplingRate);

    for (let i = 0; i + windowSize <= signal.length; i += hopSize) {
      const window = signal.slice(i, i + windowSize);
      const spectrum = SpectralAnalysisService.computePowerSpectrum(
        window,
        samplingRate,
        "hann",
      );
      const currentPower = spectrum.powerSpectrum.reduce(
        (a: number, b: number) => a + b,
        0,
      );

      // Detect movement if power increase exceeds threshold
      if (currentPower > baselinePower * params.movementBroadbandThreshold) {
        const timestamp = (i / samplingRate) * 1000;
        const duration = (windowSize / samplingRate) * 1000;

        detections.push({
          artifactId: `movement_${timestamp}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
          timestamp,
          duration,
          type: "movement",
          channels: [channelName],
          confidence:
            Math.min(
              currentPower /
                (baselinePower * params.movementBroadbandThreshold * 2),
              1.0,
            ) *
              0.8 +
            0.2,
          severity:
            currentPower > baselinePower * params.movementBroadbandThreshold * 3
              ? "severe"
              : "moderate",
          characteristics: {
            morphology: "broadband_increase",
            spatialPattern: "diffuse",
          },
        });
      }
    }

    return detections;
  }

  // --------------------------------------------------------------------------
  // Electrode Artifact Detection
  // --------------------------------------------------------------------------

  private detectElectrodeArtifact(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: DetectionParams,
  ): ArtifactDetection[] {
    const detections: ArtifactDetection[] = [];

    // Detect very high amplitude spikes (electrode pops/disconnections)
    const windowSize = Math.floor(0.1 * samplingRate); // 100ms window

    for (let i = 0; i + windowSize <= signal.length; i += windowSize) {
      const window = signal.slice(i, i + windowSize);
      const maxAmp = Math.max(...window.map(Math.abs));

      if (maxAmp > params.electrodeAmplitudeThreshold) {
        const timestamp = (i / samplingRate) * 1000;
        const duration = (windowSize / samplingRate) * 1000;

        detections.push({
          artifactId: `electrode_${timestamp}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
          timestamp,
          duration,
          type: "electrode",
          channels: [channelName],
          confidence: 0.9,
          severity: "severe",
          characteristics: {
            amplitude: maxAmp,
            morphology: "spike",
            spatialPattern: "focal",
          },
        });
      }
    }

    return detections;
  }

  // --------------------------------------------------------------------------
  // Powerline Interference Detection
  // --------------------------------------------------------------------------

  private detectPowerlineInterference(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: DetectionParams,
  ): ArtifactDetection[] {
    const detections: ArtifactDetection[] = [];

    // Compute power spectrum
    const spectrum = SpectralAnalysisService.computePowerSpectrum(
      signal,
      samplingRate,
      "hann",
    );

    // Check for power at powerline frequency and harmonics
    const powerlineFreq = params.powerlineFrequency;
    const tolerance = 1.0; // ±1 Hz

    for (const harmonic of [powerlineFreq, ...params.powerlineHarmonics]) {
      // Find power at harmonic frequency
      let harmonicPower = 0;
      for (let i = 0; i < spectrum.frequencies.length; i++) {
        if (Math.abs(spectrum.frequencies[i] - harmonic) < tolerance) {
          harmonicPower = Math.max(harmonicPower, spectrum.powerSpectrum[i]);
        }
      }

      // Compare to average power in nearby bands
      const nearbyBands = spectrum.powerSpectrum.filter(
        (_: number, idx: number) =>
          Math.abs(spectrum.frequencies[idx] - harmonic) >= 5 &&
          Math.abs(spectrum.frequencies[idx] - harmonic) <= 15,
      );
      const avgNearbyPower =
        nearbyBands.reduce((a: number, b: number) => a + b, 0) /
        nearbyBands.length;

      // Detect if harmonic power significantly exceeds nearby power
      if (harmonicPower > avgNearbyPower * 5) {
        detections.push({
          artifactId: `powerline_${harmonic}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
          timestamp: 0,
          duration: (signal.length / samplingRate) * 1000, // Entire recording
          type: "powerline",
          channels: [channelName],
          confidence:
            Math.min(harmonicPower / (avgNearbyPower * 10), 1.0) * 0.8 + 0.2,
          severity: harmonicPower > avgNearbyPower * 10 ? "severe" : "moderate",
          characteristics: {
            frequency: harmonic,
            morphology: "sinusoidal",
            spatialPattern: "diffuse",
          },
        });
      }
    }

    return detections;
  }

  // --------------------------------------------------------------------------
  // Utility Functions
  // --------------------------------------------------------------------------

  private isFrontalChannel(
    channelName: string,
    eogChannels: string[],
  ): boolean {
    return eogChannels.some((eogCh) => channelName.includes(eogCh));
  }

  private isTemporalChannel(
    channelName: string,
    emgChannels: string[],
  ): boolean {
    return emgChannels.some((emgCh) => channelName.includes(emgCh));
  }

  private findCleanEpochs(
    artifacts: ArtifactDetection[],
    totalDuration: number,
  ): Array<{ start: number; end: number; duration: number }> {
    if (artifacts.length === 0) {
      return [{ start: 0, end: totalDuration, duration: totalDuration }];
    }

    // Sort artifacts by timestamp
    const sorted = [...artifacts].sort((a, b) => a.timestamp - b.timestamp);

    const cleanEpochs: Array<{ start: number; end: number; duration: number }> =
      [];
    let currentTime = 0;

    for (const artifact of sorted) {
      const artifactStart = artifact.timestamp / 1000; // Convert to seconds
      const artifactEnd = (artifact.timestamp + artifact.duration) / 1000;

      // Clean epoch before this artifact
      if (artifactStart > currentTime) {
        cleanEpochs.push({
          start: currentTime,
          end: artifactStart,
          duration: artifactStart - currentTime,
        });
      }

      currentTime = Math.max(currentTime, artifactEnd);
    }

    // Final clean epoch after last artifact
    if (currentTime < totalDuration) {
      cleanEpochs.push({
        start: currentTime,
        end: totalDuration,
        duration: totalDuration - currentTime,
      });
    }

    return cleanEpochs;
  }

  // --------------------------------------------------------------------------
  // Parameter Presets
  // --------------------------------------------------------------------------

  static getDefaultParams(): DetectionParams {
    return {
      // EOG detection
      eogEnabled: true,
      eogFrequencyRange: [0.5, 3],
      eogAmplitudeThreshold: 100, // 100 μV
      eogChannels: ["Fp1", "Fp2", "F7", "F8"],

      // EMG detection
      emgEnabled: true,
      emgFrequencyThreshold: 20, // >20 Hz
      emgPowerThreshold: 120, // μV²
      emgChannels: ["T3", "T4", "T5", "T6", "F7", "F8"],

      // ECG detection
      ecgEnabled: true,
      ecgFrequencyRange: [0.8, 2], // 0.8-2 Hz (48-120 bpm)
      ecgRhythmicity: 0.3, // CV < 0.3
      ecgIntervalRange: [500, 1250], // 500-1250 ms (48-120 bpm)

      // Movement detection
      movementEnabled: true,
      movementBroadbandThreshold: 3, // 3x baseline
      movementMinChannels: 4, // ≥4 channels

      // Electrode artifact
      electrodeEnabled: true,
      electrodeHighFreqThreshold: 500, // >500 Hz
      electrodeAmplitudeThreshold: 50, // >50 μV (detect DC shifts/pops)

      // Powerline interference
      powerlineEnabled: true,
      powerlineFrequency: 60, // 60 Hz (North America)
      powerlineHarmonics: [120, 180], // 2nd and 3rd harmonics
    };
  }

  static getEuropeanParams(): DetectionParams {
    const params = ArtifactDetectionService.getDefaultParams();
    return {
      ...params,
      powerlineFrequency: 50, // 50 Hz (Europe, Asia, most of world)
      powerlineHarmonics: [100, 150], // 2nd and 3rd harmonics
    };
  }

  static getSensitiveParams(): DetectionParams {
    const params = ArtifactDetectionService.getDefaultParams();
    return {
      ...params,
      eogAmplitudeThreshold: 50, // Lower threshold
      emgPowerThreshold: 250,
      movementBroadbandThreshold: 2, // 2x baseline
      electrodeAmplitudeThreshold: 300,
    };
  }
}

export default ArtifactDetectionService;
